import type { Transaction } from "@/types/Transaction";
import { isFinancialCategory } from "constants/categoryGroups";
import { normalizeMerchant } from "shared/categorization/merchantNormalization";

export interface CategoryMerchantSpend {
  category: string;
  merchant: string;
  amount: number;
}

export interface SpendingInsightCandidate {
  category: string;
  merchant: string;
  currentAmount: number;
  priorAmount: number;
  increasePercent: number;
  merchantShareOfCategory: number;
}

function isExpense(tx: Transaction): boolean {
  return (tx.type ?? "expense") === "expense";
}

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function txMonthKey(tx: Transaction): string | null {
  const d = new Date(tx.date);
  if (Number.isNaN(d.getTime())) return null;
  return monthKey(d);
}

/**
 * Aggregate expense amounts by category and normalized merchant.
 */
export function aggregateCategoryMerchantSpend(
  transactions: ReadonlyArray<Transaction>,
  options?: { monthKey?: string },
): Map<string, Map<string, number>> {
  const result = new Map<string, Map<string, number>>();

  for (const tx of transactions) {
    if (!isExpense(tx)) continue;
    const key = txMonthKey(tx);
    if (!key) continue;
    if (options?.monthKey && key !== options.monthKey) continue;

    const category = tx.category?.trim() || "Uncategorized";
    const merchant = normalizeMerchant(tx.name) || tx.name.trim() || "Unknown";
    const amount = Number(tx.amount) || 0;

    if (!result.has(category)) result.set(category, new Map());
    const merchantMap = result.get(category)!;
    merchantMap.set(merchant, (merchantMap.get(merchant) ?? 0) + amount);
  }

  return result;
}

function sumCategory(map: Map<string, Map<string, number>>, category: string): number {
  const merchants = map.get(category);
  if (!merchants) return 0;
  return Array.from(merchants.values()).reduce((sum, v) => sum + v, 0);
}

/**
 * Find the strongest month-over-month spending increase with a clear merchant driver.
 */
export function findTopSpendingInsight(
  transactions: ReadonlyArray<Transaction>,
  now: Date = new Date(),
  options?: { minIncreasePercent?: number; minMerchantShare?: number },
): SpendingInsightCandidate | null {
  const minIncreasePercent = options?.minIncreasePercent ?? 20;
  const minMerchantShare = options?.minMerchantShare ?? 0.4;

  const currentMonth = monthKey(now);
  const prior = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const priorMonth = monthKey(prior);

  const current = aggregateCategoryMerchantSpend(transactions, { monthKey: currentMonth });
  const previous = aggregateCategoryMerchantSpend(transactions, { monthKey: priorMonth });

  let best: SpendingInsightCandidate | null = null;

  for (const [category, merchants] of current.entries()) {
    if (category === "Uncategorized" || category === "Other") continue;
    if (isFinancialCategory(category)) continue;

    const currentTotal = sumCategory(current, category);
    const priorTotal = sumCategory(previous, category);
    if (currentTotal <= 0) continue;

    const increasePercent = priorTotal > 0
      ? Math.round(((currentTotal - priorTotal) / priorTotal) * 100)
      : (currentTotal > 0 ? 100 : 0);

    if (increasePercent < minIncreasePercent) continue;

    let topMerchant = "";
    let topMerchantAmount = 0;
    for (const [merchant, amount] of merchants.entries()) {
      if (amount > topMerchantAmount) {
        topMerchant = merchant;
        topMerchantAmount = amount;
      }
    }

    const merchantShareOfCategory = currentTotal > 0 ? topMerchantAmount / currentTotal : 0;
    if (merchantShareOfCategory < minMerchantShare) continue;

    const candidate: SpendingInsightCandidate = {
      category,
      merchant: topMerchant,
      currentAmount: currentTotal,
      priorAmount: priorTotal,
      increasePercent,
      merchantShareOfCategory,
    };

    if (!best || candidate.increasePercent > best.increasePercent) {
      best = candidate;
    }
  }

  return best;
}

/** Top merchant within a category for analytics labels (e.g. Transport · Uber). */
export function topMerchantForCategory(
  transactions: ReadonlyArray<Transaction>,
  category: string,
  options?: { monthKey?: string; minShare?: number },
): { merchant: string; amount: number } | null {
  const minShare = options?.minShare ?? 0.35;
  const map = aggregateCategoryMerchantSpend(transactions, { monthKey: options?.monthKey });
  const catKey = category.trim() || "Uncategorized";
  const merchants = map.get(catKey);
  if (!merchants || merchants.size === 0) return null;

  let total = 0;
  let topMerchant = "";
  let topAmount = 0;
  for (const [merchant, amount] of merchants.entries()) {
    total += amount;
    if (amount > topAmount) {
      topMerchant = merchant;
      topAmount = amount;
    }
  }
  if (!topMerchant || total <= 0 || topAmount / total < minShare) return null;
  return { merchant: topMerchant, amount: topAmount };
}
